import type { JointMotion } from "@/shared/components/visualization/Panel/ThreeDPanel/BabylonManager";
import { restingJointValue } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader/jointDefaults";
import type { Joint } from "@/shared/state/visualization/schema/v1/three-d";

/**
 * Re-exported so a joint's range, its units, its motion and the value it starts
 * at are one import away from each other, and are decided once between them.
 *
 * The rule itself lives with the model loader rather than here, because the
 * import seeds a joint's link with the same value this sidebar shows — a
 * sidebar reading one number while the renderer draws another is worse than
 * both being wrong — and a feature module cannot be imported from `shared/`.
 */
export { restingJointValue };

/** The full swing offered for a joint the model declares no limits for. */
export const FREE_JOINT_RANGE_RAD = Math.PI;

/**
 * What the preview sidebar should offer for one joint.
 *
 * A `slider` row poses the joint; a `disabled` row states, in `reason`, why it
 * cannot be posed. Every joint gets a row either way — a robot whose gripper is
 * silently missing from the list reads as a conversion bug rather than as a
 * limitation of the preview.
 *
 * `motion` is carried on the slider, from {@link jointMotion}, rather than
 * worked out again at each use site: the sidebar labels radians or metres by it
 * and the renderer turns or slides the link by it, and the two must not
 * disagree.
 */
export type JointControl =
  | { kind: "slider"; min: number; max: number; motion: JointMotion }
  | { kind: "disabled"; reason: string };

/**
 * Whether a joint turns its link or slides it.
 *
 * Decided from the joint's type alone, and in one place, because three things
 * need the answer and none of them may differ from the others: the sidebar,
 * which labels radians or metres and picks a slider step; the renderer, which
 * rotates or translates; and the import, which can seed an angle but not a
 * displacement. The three non-drivable types never reach it — nothing about
 * them moves — so they are not a case here.
 */
export function jointMotion(joint: Joint): JointMotion {
  return joint.type === "prismatic" ? "slide" : "rotate";
}

/**
 * The control for a joint, per the `ROBOTO_device` extension's own
 * "Building controls from `limit`" rules.
 *
 * | Joint | Control |
 * |---|---|
 * | `revolute` with `lower < upper` | slider over `[lower, upper]`, radians |
 * | `continuous` | slider over ±π, free |
 * | `prismatic` with `lower < upper` | slider over `[lower, upper]`, metres |
 * | `prismatic` with no limits | disabled — there is no range to offer |
 * | `lower == upper` | disabled — the joint is pinned |
 * | `fixed` | disabled |
 * | `floating`, `planar` | disabled, with the reason stated |
 * | any joint with `mimic` | disabled — its value is derived, not chosen |
 *
 * Three limitations are surfaced here rather than papered over. A **coupled
 * (`mimic`) joint** does not get a control of its own: its value is a function
 * of another joint's, and a slider would let a user put a parallel gripper into
 * a pose the robot cannot reach. Nothing here applies the coupling either, so
 * such a joint stays at its rest pose — which the sidebar says outright.
 * **Floating and planar joints are not drivable**: they have 6 and 3 degrees of
 * freedom, and one number cannot express either. And a joint that declares **no
 * axis** cannot be moved along or about anything, so it gets a disabled row too
 * rather than a slider that does nothing.
 *
 * Where in that range a slider *starts* is {@link restingJointValue} and not
 * this function: the value is shared with the import, which seeds the same one
 * onto the link the joint drives, so the number the sidebar shows and the pose
 * the renderer draws come from one decision.
 *
 * A prismatic joint with no declared range is disabled rather than given the
 * free ±π a limitless revolute joint gets: π metres of travel is not a plausible
 * default for anything, and URDF requires `lower`/`upper` on a prismatic joint,
 * so their absence means a malformed description rather than a free axis.
 */
export function jointControl(joint: Joint): JointControl {
  // Before the type switch: a coupled joint of *any* type is derived, so no
  // amount of the right type or limits makes it something to drive.
  if (joint.mimic !== undefined) {
    return {
      kind: "disabled",
      reason: `Follows ${joint.mimic.joint} — coupled joints are not posed here`,
    };
  }

  switch (joint.type) {
    case "fixed":
      return { kind: "disabled", reason: "Fixed — nothing to pose" };
    case "floating":
      return {
        kind: "disabled",
        reason: "Floating joints cannot be posed in this preview yet",
      };
    case "planar":
      return {
        kind: "disabled",
        reason: "Planar joints cannot be posed in this preview yet",
      };
    case "revolute":
    case "continuous":
    case "prismatic":
      break;
  }

  if (!joint.axis) {
    return { kind: "disabled", reason: "No axis declared" };
  }

  // A continuous joint turns without end, so any limits it carries do not bound
  // it; ±π covers a full revolution either way.
  if (joint.type === "continuous") {
    return {
      kind: "slider",
      min: -FREE_JOINT_RANGE_RAD,
      max: FREE_JOINT_RANGE_RAD,
      motion: "rotate",
    };
  }

  const motion = jointMotion(joint);
  const { lower, upper } = joint;
  if (lower === undefined || upper === undefined) {
    if (motion === "slide") {
      return { kind: "disabled", reason: "No travel limits declared" };
    }
    return {
      kind: "slider",
      min: -FREE_JOINT_RANGE_RAD,
      max: FREE_JOINT_RANGE_RAD,
      motion,
    };
  }
  if (lower === upper) {
    return { kind: "disabled", reason: "Pinned by its limits" };
  }
  if (lower > upper) {
    return { kind: "disabled", reason: "Limits are inverted" };
  }
  return { kind: "slider", min: lower, max: upper, motion };
}

/** Whether any joint in the model can actually be posed. */
export function hasPosableJoint(joints: Joint[]): boolean {
  return joints.some((joint) => jointControl(joint).kind === "slider");
}
